<template>
  <div class="coupon-property-user-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('couponPropertyUserManage.page.title')"
      :subtitle="$t('couponPropertyUserManage.page.subtitle')" />

    <!-- (2) SearchPanel：筛选字段与含义保持不变，仅重排栅格并统一动作区 -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.couponId" clearable
            :placeholder="$t('couponPropertyUserManage.search.couponId')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.couponName" clearable
            :placeholder="$t('couponPropertyUserManage.search.couponName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.validityDay" clearable
            :placeholder="$t('couponPropertyUserManage.search.validityDay')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.couponUserName" clearable
            :placeholder="$t('couponPropertyUserManage.search.userName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.tel" clearable
            :placeholder="$t('couponPropertyUserManage.search.tel')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.toType" :placeholder="$t('couponPropertyUserManage.search.toType')" clearable
            class="w-full">
            <el-option :label="$t('couponPropertyUserManage.search.selectToType')" value="" />
            <el-option :label="$t('couponPropertyUserManage.search.shopping')" value="1001" />
            <el-option :label="$t('couponPropertyUserManage.search.payment')" value="2002" />
            <el-option :label="$t('couponPropertyUserManage.search.repair')" value="3003" />
            <el-option :label="$t('couponPropertyUserManage.search.parking')" value="4004" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.state" :placeholder="$t('couponPropertyUserManage.search.state')" clearable
            class="w-full">
            <el-option :label="$t('couponPropertyUserManage.search.selectState')" value="" />
            <el-option :label="$t('couponPropertyUserManage.search.unused')" value="1001" />
            <el-option :label="$t('couponPropertyUserManage.search.used')" value="2002" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('couponPropertyUserManage.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('couponPropertyUserManage.page.empty')" @retry="getList">
        <el-table-column prop="couponId" :label="$t('couponPropertyUserManage.table.couponId')" align="center"
          width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.couponId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="couponName" :label="$t('couponPropertyUserManage.table.couponName')" align="left"
          min-width="160" show-overflow-tooltip />
        <el-table-column :label="$t('couponPropertyUserManage.table.value')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.value) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('couponPropertyUserManage.table.validityDay')" align="right" width="100">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatNumber(scope.row.validityDay, 0) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="userName" :label="$t('couponPropertyUserManage.table.userName')" align="center"
          width="100" />
        <el-table-column prop="tel" :label="$t('couponPropertyUserManage.table.tel')" align="center" width="130" />
        <el-table-column prop="toTypeName" :label="$t('couponPropertyUserManage.table.toType')" align="center"
          width="100" />
        <el-table-column :label="$t('couponPropertyUserManage.table.quantity')" align="right" width="100">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatNumber(scope.row.stock, 0) }}</span>
            <span class="ds-cell-sub">{{ $t('couponPropertyUserManage.table.sheet') }}</span>
          </template>
        </el-table-column>
        <!-- 状态 Tag：未使用 / 已使用（颜色仅作辅助，始终带文字，满足 WCAG 1.4.1） -->
        <el-table-column :label="$t('couponPropertyUserManage.table.state')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveCouponState(scope.row)" :label="resolveCouponStateLabel(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column prop="startTime" :label="$t('couponPropertyUserManage.table.startTime')" align="center"
          width="160" />
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>
  </div>
</template>

<script>
import { listCouponPropertyUser } from '@/api/scm/couponPropertyUserManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import { formatNumber, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CouponPropertyUserManageList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        couponId: '',
        couponName: '',
        validityDay: '',
        couponUserName: '',
        tel: '',
        toType: '',
        state: '',
        communityId: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.searchForm.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.searchForm
        }
        const res = await listCouponPropertyUser(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.tableData = Array.isArray(res.data) ? res.data : []
        this.page.total = res.records || res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('couponPropertyUserManage.page.loadFailed')
        console.error('Failed to load owner coupons:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    /**
     * 重置筛选：仅清空可筛选字段，保留 communityId。
     */
    handleReset() {
      this.searchForm.couponId = ''
      this.searchForm.couponName = ''
      this.searchForm.validityDay = ''
      this.searchForm.couponUserName = ''
      this.searchForm.tel = ''
      this.searchForm.toType = ''
      this.searchForm.state = ''
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    },
    /**
     * 金额格式化（只改展示，不改原值）
     */
    formatMoney(value) {
      return formatMoney(value)
    },
    /**
     * 数量格式化（只改展示，不改原值）
     */
    formatNumber(value, decimals = 2) {
      return formatNumber(value, decimals)
    },
    /**
     * 优惠券状态语义类型（1001 未使用 / 2002 已使用）
     */
    resolveCouponState(row) {
      if (!row) return 'muted'
      return row.state === '2002' ? 'success' : 'warning'
    },
    /**
     * 优惠券状态文案
     */
    resolveCouponStateLabel(row) {
      if (!row) return '-'
      return row.state === '2002'
        ? this.$t('couponPropertyUserManage.table.used')
        : this.$t('couponPropertyUserManage.table.unused')
    }
  }
}
</script>

<style lang="scss" scoped>
.coupon-property-user-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  margin-left: 2px;
}
</style>
